import {
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  DollarSign,
  Briefcase,
  Edit2,
  XCircle,
  CheckCircle,
} from 'lucide-react';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TRANSACTION_TYPES = [
  { value: 'credit', label: 'Credit (money in)', color: 'bg-green-50 text-green-700', icon: ArrowUpCircle },
  { value: 'debit', label: 'Debit (money out)', color: 'bg-red-50 text-red-700', icon: ArrowDownCircle },
  { value: 'investment', label: 'Investment', color: 'bg-green-100 text-green-700', icon: ArrowUpCircle },
  { value: 'deal_purchase', label: 'Deal Purchase', color: 'bg-indigo-100 text-indigo-700', icon: Briefcase },
  { value: 'bank_interest', label: 'Bank Interest', color: 'bg-lime-100 text-lime-800', icon: TrendingUp },
  { value: 'deal_repayment', label: 'Deal Repayment', color: 'bg-purple-100 text-purple-700', icon: DollarSign },
  { value: 'withdrawal', label: 'Withdrawal', color: 'bg-amber-100 text-amber-700', icon: ArrowDownCircle },
  { value: 'fee', label: 'Fee (legacy)', color: 'bg-gray-100 text-gray-700', icon: DollarSign },
  { value: 'fee_mgmt', label: 'Management Fee', color: 'bg-stone-100 text-stone-700', icon: DollarSign },
  { value: 'fee_perf', label: 'Performance Fee', color: 'bg-zinc-100 text-zinc-700', icon: DollarSign },
  { value: 'fee_rebate', label: 'Fee Rebate', color: 'bg-teal-100 text-teal-700', icon: ArrowUpCircle },
  { value: 'adjustment', label: 'Adjustment (legacy)', color: 'bg-slate-100 text-slate-700', icon: Edit2 },
  { value: 'adjustment_up', label: 'Adjustment +', color: 'bg-emerald-100 text-emerald-700', icon: ArrowUpCircle },
  { value: 'adjustment_down', label: 'Adjustment -', color: 'bg-rose-100 text-rose-700', icon: ArrowDownCircle },
  { value: 'write_off', label: 'Write Off', color: 'bg-orange-100 text-orange-700', icon: XCircle },
  { value: 'verified_no_match', label: 'Verified (no match)', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
];

export const getTransactionConfig = (type) => {
  const found = TRANSACTION_TYPES.find(t => t.value === type);
  if (found) return found;
  // Unknown type — render the raw string as the label, neutral colour. Don't
  // silently fall back to "Investment" (which masks bad data).
  return {
    value: type || 'unknown',
    label: type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown',
    color: 'bg-slate-100 text-slate-700',
    icon: Edit2,
  };
};

export const formatCurrency = (value) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(value || 0);

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// A FastAPI validation error (422) returns `detail` as an ARRAY of
// {loc, msg, type} objects, not a string — every other error response
// returns it as a plain string. Passing the array straight to toast.error()
// crashes the whole page (React throws "Objects are not valid as a React
// child" with no error boundary to catch it — see App.js's new
// ErrorBoundary, 2026-09-30, added after exactly this crash on Force Clean
// when a bulk selection exceeded the endpoint's max item limit). Always
// route an error response through this before showing it.
export const apiErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (detail && typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Bulk-delete response → accurate toast (deleted vs refused, with reasons).
// The server is the source of truth: it can refuse some rows (e.g. a loan
// cashflow that is reconciled, or that came from New Deal / the workbook).
export const reportBulkDelete = (toast, data) => {
  const n = data?.deleted_count ?? 0;
  const skipped = data?.skipped || [];
  if (n > 0) toast.success(`${n} deleted`);
  if (skipped.length) {
    const reasons = [...new Set(skipped.map(s => s.reason))];
    toast.error(`${skipped.length} not deleted: ${reasons.join('; ')}`, { duration: 12000 });
  }
  if (n === 0 && !skipped.length) toast.error('Nothing was deleted');
};

// Bulk verify response → toast with the server's per-row reasons (the old
// "N failed — check console" message never logged anything to the console).
export const reportBulkVerify = (toast, data) => {
  const ok = data?.verified_count ?? 0;
  const failed = data?.failed || [];
  if (ok > 0) toast.success(`Verified ${ok}`);
  if (failed.length) {
    const reasons = [...new Set(failed.map(f => f.error || 'unknown error'))];
    toast.error(`${failed.length} not verified: ${reasons.join('; ')}`, { duration: 15000 });
    console.warn('Bulk verify failures', failed);
  }
  if (!ok && !failed.length) toast.error('Nothing was verified');
};
